// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useCallback, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { CollateralAgreement } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Model/Collateral';
import { UserService } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Service/User';
import { EligibleInstrument } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Types/module';
import { ContractId } from '@daml/types';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import { useModuleParty } from '../../../contexts/ModuleContext';
import { useExerciseSubmission } from '../../../hooks/mutations/ledger/useSubmission';
import useCollateralAgreementRequests from '../../../hooks/queries/collateral/collateral/useCollateralAgreementRequests';
import useCollateralAgreements from '../../../hooks/queries/collateral/collateral/useCollateralAgreements';
import { useCollateralUserService } from '../../../hooks/queries/collateral/onboarding/useUserService';
import { ERR_REQ_AGR, ERR_REQ_CHANGE, SUC_REQ_AGR, SUC_REQ_CHANGE } from '../../../utils/strings';
import { CollateralAgreementRow } from '../collaterals/types';
import { InstrumentMultiSelect } from './InstrumentMultiSelect';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  row?: CollateralAgreementRow;
};

const RequestCollateralAgreementDialog = ({ open, onClose, row }: DialogProps) => {
  const [counterparty, setCounterparty] = useState<string>(row?.payload.counterparty || '');
  const agreementIds = useCollateralAgreements().data?.map((agreement) => agreement.payload.id);
  const party = useModuleParty();
  const pendingAgreementIds = useCollateralAgreementRequests().data?.map(
    (pending) => pending.payload.id,
  );

  const [eligibleInstruments, setEligibleInstruments] = React.useState(
    row?.payload.terms.eligibleCollateral.eligibleInstruments.map((i) => i) || [],
  );
  const isModified =
    JSON.stringify(eligibleInstruments) !==
    JSON.stringify(row?.payload.terms.eligibleCollateral.eligibleInstruments);
  const allAgreementIds = agreementIds?.concat(pendingAgreementIds || []);
  const [agreementId, setAgreementId] = useState<string>(row?.payload.id || '');

  const userService = useCollateralUserService();

  const { submit: requestCollateralAgreement } = useExerciseSubmission(
    UserService.UserService_RequestCollateralAgreement,
    SUC_REQ_AGR,
    ERR_REQ_AGR,
  );

  const { submit: changeCollateralAgreement } = useExerciseSubmission(
    UserService.UserService_ProposeCollateralAgreementChange,
    SUC_REQ_CHANGE,
    ERR_REQ_CHANGE,
  );

  const resetState = useCallback(() => {
    if (row) {
      return;
    }
    setCounterparty('');
    setAgreementId('');
    setEligibleInstruments([]);
  }, [setCounterparty, setAgreementId, row]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleChange = useCallback(async () => {
    if (!userService.data) throw new Error('Trader service not found');
    if (!row?.contractId) throw new Error('No Collateral Agreement Cid defined');

    await changeCollateralAgreement({
      cid: userService.data.contractId,
      argument: {
        cid: row?.contractId as ContractId<CollateralAgreement>,
        updatedTerms: {
          eligibleCollateral: {
            eligibleInstruments,
          },
        },
      },
    });
    handleClose();
  }, [
    userService.data,
    handleClose,
    changeCollateralAgreement,
    row?.contractId,
    eligibleInstruments,
  ]);

  const handleSubmit = useCallback(async () => {
    if (!userService.data) throw new Error('Trader service not found');

    await requestCollateralAgreement({
      cid: userService.data.contractId,
      argument: {
        counterparty,
        id: agreementId,
        requestorIsPartyA: true,
        terms: {
          eligibleCollateral: {
            eligibleInstruments,
          },
        },
      },
    });
    handleClose();
  }, [
    userService.data,
    counterparty,
    agreementId,
    handleClose,
    requestCollateralAgreement,
    eligibleInstruments,
  ]);

  const onRemove = (inst: EligibleInstrument) => {
    setEligibleInstruments((p) => {
      const updated = [] as EligibleInstrument[];
      p.forEach((i) => {
        if (
          i.instrument.id !== inst.instrument.id ||
          i.instrument.admin !== inst.instrument.admin
        ) {
          updated.push(i);
        }
      });
      return updated;
    });
  };

  const onSelect = (inst: EligibleInstrument) => {
    const isSelected = eligibleInstruments.filter(
      (i) => i.instrument.id === inst.instrument.id && i.instrument.admin && inst.instrument.admin,
    ).length;
    if (isSelected) {
      onRemove(inst);
    } else {
      setEligibleInstruments((p) => [...p, inst]);
    }
  };

  const [error, setError] = React.useState('');
  const [counterpartyErrorMsg, setCounterpartyErrorMsg] = React.useState('');
  const isLoading = userService.isLoading || !userService.data;

  if (isLoading) return <CircularProgress />;
  const checkUnique = (s: string) => {
    if (allAgreementIds?.includes(s)) {
      return 'This agreement Id exists already. Please ensure the agreement Ids are unique.';
    }
    return '';
  };
  const counterPartyValidation = (s: string) => {
    if (s === party) {
      return 'Counterparty cannot be your own party ID';
    }
    return '';
  };
  const disableSubmit =
    (row ? !isModified : false) ||
    !counterparty ||
    !agreementId ||
    error.length > 0 ||
    eligibleInstruments.length <= 0;
  return (
    <FormDialog
      open={open}
      title={row ? 'Modify collateral agreement' : 'Create a collateral agreement'}
      onCancel={handleClose}
      onSubmit={row ? handleChange : handleSubmit}
      submitName={row ? 'Modify' : 'Create'}
      isSubmitDisabled={disableSubmit}
    >
      <TextInput
        enableTopLabel
        placeholder="Enter an identifier for this agreement"
        label="Agreement Id"
        value={agreementId}
        getValidationMessage={checkUnique}
        setValidationMessage={setError}
        validationMessage={error}
        disabled={!!row}
        setValue={setAgreementId}
        required
      />
      <TextInput
        enableTopLabel
        placeholder="Enter the counterparty party ID"
        label="Counterparty (Party B)"
        caption="Enter the receiving party ID. By default, this will be Party B."
        value={counterparty}
        validationMessage={counterpartyErrorMsg}
        setValidationMessage={setCounterpartyErrorMsg}
        getValidationMessage={counterPartyValidation}
        disabled={!!row}
        setValue={setCounterparty}
        required
      />
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex' }}>
          <Typography
            fontWeight="bold"
            variant="caption"
            color="secondary"
            sx={{ textTransform: 'uppercase' }}
          >
            Eilligble collateral
          </Typography>
          <Typography color="error">*</Typography>
        </Box>
        <Typography variant="caption" color="textSecondary" sx={{ mb: 1 }}>
          Select a collateral type for this agreement.
        </Typography>
      </Box>
      <InstrumentMultiSelect
        onRemove={onRemove}
        selectedInstruments={eligibleInstruments}
        onClick={onSelect}
      />
    </FormDialog>
  );
};
export const MemoizedRequestCollateralAgreementDialog = React.memo(
  RequestCollateralAgreementDialog,
);
